import { useState, useRef, useEffect } from 'react';
import {
    HiChevronDown,
    HiChevronUp,
    HiExternalLink,
} from 'react-icons/hi';
import { MdAccountTree } from 'react-icons/md';
import {
    getDashboardStatusColor,
    getDashboardStatusLabel,
    dashboardStatusHexColors,
} from '../../../constants/integrationTypes';
import Portal from '../../../components/Portal';
import CustomTooltip from '../../../components/common/CustomTooltip';

// Interface for predecessor items (matches backend structure)
export interface PredecessorItem {
    task_id: string;
    title: string;
    status: string;
    project_id: string;
}

interface PredecessorDropdownProps {
    predecessorData: PredecessorItem[];
    currentProjectId: string;
    onTaskClick: (projectId: string, taskId: string) => void;
    taskStatus?: string;
}

interface DropdownPosition {
    top: number;
    left: number;
}

const PredecessorDropdown = ({
    predecessorData,
    currentProjectId,
    onTaskClick,
    taskStatus = '',
}: PredecessorDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Calculate position when dropdown opens
    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const dropdownWidth = 384; // w-96 = 384px
            const estimatedDropdownHeight = Math.min(predecessorData.length * 70 + 100, 400); // Estimate based on items

            // Calculate left position - try to center under button, but ensure it stays in viewport
            let left = rect.left + (rect.width / 2) - (dropdownWidth / 2);

            // Ensure dropdown doesn't go off left edge
            if (left < 8) {
                left = 8;
            }

            // Ensure dropdown doesn't go off right edge
            if (left + dropdownWidth > window.innerWidth - 8) {
                left = window.innerWidth - dropdownWidth - 8;
            }

            // Calculate top position - check if there's enough space below
            let top = rect.bottom + 4; // Default: 4px gap below button
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // If not enough space below but more space above, position above the button
            if (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow) {
                top = rect.top - estimatedDropdownHeight - 4;
                // Make sure it doesn't go above viewport
                if (top < 8) {
                    top = 8;
                }
            }

            setPosition({
                top: top,
                left: left,
            });
        }
    }, [isOpen, predecessorData.length]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    if (!predecessorData || predecessorData.length === 0) {
        return null;
    }

    // Status priority for sorting (lower = higher priority, shown first)
    // Uses only DashboardStatus values from dashboardStatusColors
    const getStatusPriority = (status: string): number => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            // Critical statuses - show first
            case 'delayed':
                return 1;
            case 'unallocated':
                return 2;
            case 'blocked':
                return 3;
            case 'at_risk':
                return 4;
            // Pending statuses
            case 'not_started':
                return 5;
            case 'unassigned':
                return 6;
            // Active/positive statuses
            case 'on_track':
                return 7;
            // Completed statuses - show last
            case 'completed':
            case 'closed':
                return 8;
            default:
                return 9;
        }
    };

    // Sort predecessors by status priority
    const sortedPredecessors = [...predecessorData].sort((a, b) => {
        return getStatusPriority(a.status) - getStatusPriority(b.status);
    });

    // Get button color - only red when status is 'blocked', otherwise neutral
    const getButtonStyle = () => {
        const statusLower = taskStatus?.toLowerCase() || '';
        if (statusLower === 'blocked') {
            return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700 hover:bg-red-200 dark:hover:bg-red-900/50';
        }
        // Default neutral color
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700/50';
    };

    return (
        <div className="relative inline-block">
            <button
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium border transition-all duration-150 shadow-sm ${getButtonStyle()}`}
            >
                <MdAccountTree className="w-3.5 h-3.5" />
                <span>Predecessors ({predecessorData.length})</span>
                {isOpen ? (
                    <HiChevronUp className="w-3.5 h-3.5" />
                ) : (
                    <HiChevronDown className="w-3.5 h-3.5" />
                )}
            </button>

            {isOpen && (
                <Portal>
                    <div
                        ref={dropdownRef}
                        className="fixed w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden ring-1 ring-black/5"
                        style={{
                            top: position.top,
                            left: position.left,
                            zIndex: 9999,
                        }}
                    >
                        {/* Header */}
                        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/80 dark:to-gray-800/50">
                            <div className="flex items-center gap-2">
                                <MdAccountTree className="w-4 h-4 text-primary-dark dark:text-primary-light" />
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Predecessor Tasks
                                </span>
                                <span className="ml-auto text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                                    {predecessorData.length}
                                </span>
                            </div>
                        </div>

                        {/* List */}
                        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/50">
                            {sortedPredecessors.map((predecessor, index) => {
                                const statusColor = dashboardStatusHexColors[predecessor.status?.toLowerCase()] || '#9ca3af';
                                const statusLabel = getDashboardStatusLabel(predecessor.status);

                                return (
                                    <CustomTooltip
                                        key={predecessor.task_id || index}
                                        title={predecessor.title}
                                        placement="top"
                                    >
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onTaskClick(predecessor.project_id, predecessor.task_id);
                                                setIsOpen(false);
                                            }}
                                            className="group w-full flex cursor-pointer transition-all duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                                        >
                                            {/* Status line on left with rounded corners */}
                                            <div
                                                className="w-1 flex-shrink-0 rounded-r-full my-2"
                                                style={{ backgroundColor: statusColor }}
                                            />

                                            {/* Content */}
                                            <div className="flex-1 min-w-0 py-3 pl-3 pr-2">
                                                {/* Title - main focus */}
                                                <p className="text-sm text-gray-800 dark:text-gray-100 font-medium leading-snug line-clamp-2 group-hover:text-primary-dark dark:group-hover:text-primary-light transition-colors">
                                                    {predecessor.title}
                                                </p>

                                                {/* Meta info row */}
                                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                    <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700/50 px-1.5 py-0.5 rounded font-mono">
                                                        {predecessor.task_id}
                                                    </span>
                                                    {statusLabel && (
                                                        <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded-full font-medium ${getDashboardStatusColor(predecessor.status)}`}>
                                                            {statusLabel}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Arrow icon */}
                                            <div className="flex items-center px-3 text-gray-300 dark:text-gray-600 group-hover:text-primary-dark dark:group-hover:text-primary-light transition-colors">
                                                <HiExternalLink className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </CustomTooltip>
                                );
                            })}
                        </div>

                        {/* Footer hint */}
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 text-center">
                                Click to open task in new tab
                            </p>
                        </div>
                    </div>
                </Portal>
            )}
        </div>
    );
};

export default PredecessorDropdown;
